const MOBILE_QUERY = '(max-width: 749px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

class GigaCharacters extends HTMLElement {
  // A hidden tab keeps compositing CSS animations but starves the timer, so stop
  // entirely while away and start a fresh crossing on return. Declared as a field
  // so add/removeEventListener always see the same bound function.
  onVisibilityChange = () => {
    if (document.hidden) {
      clearTimeout(this.timer);
      this.replaceChildren();
    } else {
      this.scheduleNext(this.randomBetween(0.6, 2));
    }
  };

  connectedCallback() {
    if (window.matchMedia(REDUCED_MOTION_QUERY).matches) return;

    this.sources = this.parseSources();
    if (this.sources.length === 0) return;

    this.settings = {
      size: parseFloat(this.dataset.size),
      sizeMobile: parseFloat(this.dataset.sizeMobile),
      crossingMin: parseFloat(this.dataset.crossingMin),
      crossingMax: parseFloat(this.dataset.crossingMax),
      gapMin: parseFloat(this.dataset.gapMin),
      gapMax: parseFloat(this.dataset.gapMax),
      topMin: parseFloat(this.dataset.topMin),
      topMax: parseFloat(this.dataset.topMax),
      bob: parseFloat(this.dataset.bob),
      opacity: parseFloat(this.dataset.opacity),
      direction: this.dataset.direction,
    };

    this.queue = [];
    this.timer = null;

    this.preload();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.scheduleNext(this.randomBetween(0.6, 2));
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.replaceChildren();
  }

  parseSources() {
    try {
      const sources = JSON.parse(this.dataset.sources);
      return Array.isArray(sources) ? sources.filter((source) => source && source.src) : [];
    } catch {
      return [];
    }
  }

  preload() {
    this.sources.forEach((source) => {
      const image = new Image();
      image.fetchPriority = 'low';
      image.src = source.src;
    });
  }

  scheduleNext(delayInSeconds) {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.cross(), delayInSeconds * 1000);
  }

  /** Sources are shuffled per cycle so every character shows up before any repeats. */
  nextSource() {
    if (this.queue.length === 0) {
      this.queue = [...this.sources];
      for (let i = this.queue.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [this.queue[i], this.queue[j]] = [this.queue[j], this.queue[i]];
      }
    }
    return this.queue.pop();
  }

  nextDirection() {
    if (this.settings.direction === 'random') {
      return Math.random() < 0.5 ? 'right' : 'left';
    }
    return this.settings.direction;
  }

  randomBetween(min, max) {
    return min + Math.random() * (max - min);
  }

  cross() {
    const source = this.nextSource();
    const direction = this.nextDirection();
    const isMobile = window.matchMedia(MOBILE_QUERY).matches;
    const duration = this.randomBetween(this.settings.crossingMin, this.settings.crossingMax);
    const offscreen = { start: 'calc(-100% - 4cqw)', end: 'calc(100cqw + 4cqw)' };

    const character = document.createElement('div');
    character.className = 'giga-character';
    character.style.setProperty('--giga-top', `${this.randomBetween(this.settings.topMin, this.settings.topMax)}%`);
    character.style.setProperty('--giga-size', `${isMobile ? this.settings.sizeMobile : this.settings.size}px`);
    character.style.setProperty('--giga-opacity', `${this.settings.opacity}`);
    character.style.setProperty('--giga-duration', `${duration}s`);
    character.style.setProperty('--giga-from', direction === 'right' ? offscreen.start : offscreen.end);
    character.style.setProperty('--giga-to', direction === 'right' ? offscreen.end : offscreen.start);
    // The artwork faces right, so a leftward crossing is mirrored.
    character.style.setProperty('--giga-flip', direction === 'right' ? '1' : '-1');
    character.style.setProperty('--giga-bob', `${this.settings.bob}px`);
    character.style.setProperty('--giga-bob-duration', `${this.randomBetween(0.9, 1.5)}s`);
    character.style.setProperty('--giga-tilt', `${this.randomBetween(1.5, 4)}deg`);

    const bob = document.createElement('span');
    bob.className = 'giga-character__bob';

    const image = document.createElement('img');
    image.className = 'giga-character__image';
    image.src = source.src;
    image.width = source.width;
    image.height = source.height;
    image.alt = '';
    image.decoding = 'async';
    image.setAttribute('aria-hidden', 'true');

    bob.appendChild(image);
    character.appendChild(bob);
    character.addEventListener('animationend', (event) => {
      if (event.target === character) character.remove();
    });
    this.appendChild(character);

    // One character at a time: wait out this crossing before starting the next.
    this.scheduleNext(duration + this.randomBetween(this.settings.gapMin, this.settings.gapMax));
  }
}

customElements.define('giga-characters', GigaCharacters);
